import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1041SchI = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="1041 Schedule I - AMT Filing Guide for Estates and Trusts"
          description="Explore 1041 Schedule I for calculating the Alternative Minimum Tax for estates and trusts. Learn about filing requirements and how TaxZerone can help."
          keywords=""
          canonical="resources/tax-exempt/1041-schedule-i-amt/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          1041 Schedule I{' '}
          <span className="highlight-container">
            <span className="highlight">Alternative Minimum Tax</span>
          </span>
          <br />
          Estates and Trusts
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/1041-schedule-i-amt/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule I (Form 1041) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Schedule I (Form 1041)
                        </strong>{' '}
                        is used to calculate{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Alternative Minimum Tax (AMT)
                        </strong>{' '}
                        for an estate or trust and determine the amount of AMT,
                        if any, reported on{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1041.</strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Estates and trusts filing{' '}
                        <strong style={{ fontWeight: 600 }}> Form 1041</strong>{' '}
                        generally complete{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule I </strong>
                        when they are subject to the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          alternative minimum tax{' '}
                        </strong>
                        or must complete the AMT calculation under the
                        applicable IRS requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule I calculates
                        <strong style={{ fontWeight: 600 }}>
                          alternative minimum taxable income (AMTI),
                        </strong>{' '}
                        applicable{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          AMT adjustments and preferences, the tentative minimum
                          tax,
                        </strong>
                        and the resulting{' '}
                        <strong style={{ fontWeight: 600 }}>
                          alternative minimum tax.{' '}
                        </strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Related Tax Filing:
                        </strong>{' '}
                        Organizations with{' '}
                        <strong style={{ fontWeight: 600 }}>
                          unrelated business income
                        </strong>{' '}
                        may also need to review{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>{' '}
                        and its applicable schedules for reporting and tax
                        requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Introduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule I (Form 1041)
                  </strong>{' '}
                  is a supplemental form used by estates and trusts when filing
                  their annual income tax returns (Form 1041). It calculates the
                  alternative minimum tax (AMT) that may apply to the estate or
                  trust.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule I is often used in conjunction with{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-T
                  </Link>
                  , which is filed by tax-exempt organizations. If an estate or
                  trust is also tax-exempt, it may need to file both 1041 tax
                  form and{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    form 990 t
                  </Link>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  In this resource guide, we will learn about the key aspects of
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule I (Form 1041)
                  </span>
                  , from its purpose to filing requirements and commonly asked
                  questions.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule I (Form 1041)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule I (Form 1041)?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Schedule I (Form 1041) filing requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule I (Form 1041)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule I (Form 1041)
                  </span>{' '}
                  is a supplemental form used to calculate the{' '}
                  <span style={{ fontWeight: 600 }}>
                    alternative minimum tax (AMT)
                  </span>{' '}
                  for estates and trusts. The form adjusts taxable income by
                  adding back items that are treated differently under the AMT
                  rules, such as
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    depreciation or passive activity losses
                  </span>
                  . This ensures that the estate or trust pays at least the
                  minimum amount of tax required by law.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  It includes several lines for entering{' '}
                  <span style={{ fontWeight: 600 }}>
                    various types of income, adjustments, preferences, and
                    exemptions
                  </span>{' '}
                  that are used to determine the AMT.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Streamline Your 990-T Filing with TaxZerone!
                      </Typography>
                      <Typography variant="body2">
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1041schi`}
                      >
                        E-File Form 990-T
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule I (Form 1041)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule I (Form 1041)
                  </span>{' '}
                  must be filed by estates and trusts that have certain types of
                  income or deductions that could trigger the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Alternative Minimum Tax (AMT)
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Specifically, if the estate or trust has an income that
                  exceeds the exemption amount or claims significant deductions
                  that reduce its regular tax liability, filing{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule I is mandatory
                  </span>
                  . The exemption amount varies depending on the filing status
                  of the estate or trust.
                </Typography>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="choose"
                >
                  Schedule I (Form 1041) filing requirements
                </Typography>

                <picture>
                  <source srcSet="/assets/images/schi-f1041-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-img.png"
                    loading="lazy"
                    alt="Schedule I (Form 1041) filing requirement "
                    width={760}
                    height={113}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This Schedule I (Form 1041) is used to calculate the
                  Alternative Minimum Tax (AMT) for estates and trusts to
                  determine if any additional tax is owed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Name of estate or trust
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the name of the estate or trust filing the return.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Employer Identification Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Provide the EIN assigned to the estate or trust for tax
                  identification.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Below, we have provided Schedule I (Form 1041) filing
                  requirements for each part.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I-Estate’s or Trust’s share of Alternative Minimum
                  Taxable Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part1-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part1-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part I showing the estate’s or trust’s share of alternative minimum taxable income (AMTI) for calculating AMT. Line1-Line15"
                    width={760}
                    height={258}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the estate’s or trust’s adjusted total income or loss
                  taken from Form 1041, line 17. This amount serves as the base
                  figure that will later be adjusted to determine the
                  Alternative Minimum Tax income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any interest-related adjustments that must be
                  recalculated under AMT rules. Some interest expenses that are
                  allowed for regular tax may need to be treated differently
                  when figuring AMT.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    state, local, or foreign taxes that were included
                  </span>{' '}
                  on Form 1041. These can include property taxes, sales taxes,
                  or income taxes paid to state or local authorities.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust received a refund of taxes in the
                  current year that were previously deducted, enter that amount
                  here. It usually reduces the earlier tax adjustment.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust claimed depletion for natural resources
                  (like oil, gas, or minerals), it must be recalculated using
                  AMT rules. Enter the difference between the regular tax
                  deduction and the AMT deduction.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust used a net operating loss to lower its
                  regular taxes, the IRS requires you to add that amount back
                  here as a positive number. Essentially, the AMT rules don't
                  let you use this deduction the same way regular tax rules do.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Usually, interest from municipal bonds is tax-free. However,
                  private activity bonds are an exception for the AMT. If you
                  earned interest here, you have to count it as taxable income
                  for this section.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you sold small business stock and took a tax break on the
                  profit, the AMT wants a small piece of it back. You’ll need to
                  add 7% of that excluded gain back into your totals here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In the regular tax world, you aren't taxed just for buying an
                  ISO. But for the AMT, the IRS looks at the "bargain element"
                  the difference between what you paid and what the stock is
                  actually worth and treats that discount as taxable
                  income.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If this estate is a beneficiary of another trust, check the
                  Schedule K-1 you received from them. Look at Box 12 (Code A);
                  if there’s a number there, it’s an AMT adjustment that you
                  need to carry over to this line.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust sold or disposed of property, the gain
                  or loss may be calculated differently under AMT rules. Enter
                  the difference between the regular tax result and the AMT
                  result.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Some assets use different depreciation methods for AMT
                  purposes. Recalculate depreciation using the AMT rules and
                  enter the difference between the regular tax depreciation and
                  AMT depreciation.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust has{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passive income or losses,
                  </span>{' '}
                  such as from certain investments or partnerships, they must be
                  recalculated using AMT rules. Enter the difference between the
                  regular tax amount and the AMT amount.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Losses from activities where the estate or trust is not fully
                  at risk, or from certain partnerships or S corporations, may
                  be limited. Recalculate the allowable loss using AMT rules and
                  enter the difference.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For businesses like publishing, circulation expenses deducted
                  immediately for regular tax must instead be spread over three
                  years for AMT purposes. Enter the difference between the
                  regular deduction and the AMT deduction.{' '}
                </Typography>

                <picture>
                  <source srcSet="/assets/images/schi-f1041-part1-line16-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part1-line16-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part1-line16-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part I showing the estate’s or trust’s share of alternative minimum taxable income (AMTI) for calculating AMT. Line16-Line27"
                    width={760}
                    height={305}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust has income from long-term contracts,
                  the income may be calculated differently under AMT rules.
                  Enter the difference between the income reported for regular
                  tax and the amount calculated for AMT.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If there are expenses related to mining activities, these
                  costs may be treated differently for AMT. Enter the difference
                  between the deduction allowed for regular tax and the
                  deduction allowed under AMT rules.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Costs spent on research or experimental activities may have
                  different deduction rules for AMT. Record the difference
                  between the regular tax deduction and the AMT deduction.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the estate or trust still receives income from installment
                  sales made before January 1, 1987, this income may need an
                  adjustment for AMT. Enter the amount that must be included for
                  AMT purposes.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For oil and gas drilling activities, some intangible drilling
                  costs that were deducted for regular tax may be treated as a
                  tax preference for AMT. Enter the portion that must be added
                  back when calculating AMT income.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any additional adjustments that affect income for AMT
                  purposes. This may include items that must be added or changed
                  when calculating income under AMT rules.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Alternative Tax Net Operating Loss Deduction
                  (ATNOLD) allowed under AMT rules if the estate or trust has a
                  net operating loss that can be used for AMT, following the
                  applicable AMT limitation rules.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 23
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add together all the amounts from lines 1 through 22. The
                  result is the estate or trust’s adjusted income calculated
                  under AMT rules.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 24
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the income distribution deduction amount calculated in
                  Part II, line 42. This represents the portion of income
                  distributed to beneficiaries that reduces AMT income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 25
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write the estate tax deduction taken on Form 1041, line 19.
                  This amount is allowed as a deduction when calculating AMT
                  income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from lines 24 and 25. This shows the total
                  deductions that will be subtracted from AMT income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 26 from line 23. The result is the estates or
                  trust’s alternative minimum taxable income.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II-Income Distribution Deduction on a Minimum Tax Basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part2-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part2-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part II showing the income distribution deduction calculated on a minimum tax (AMT) basis for estates and trusts. Line28-Line40"
                    width={760}
                    height={259}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Start with the amount from line 23 and add back any Section
                  199A deduction, because this deduction is not allowed when
                  calculating AMT distribution income. This adjusted figure is
                  used to determine the income distribution deduction under AMT
                  rules.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the tax-exempt interest after making AMT
                  adjustments. Subtract any related expenses that were used to
                  earn that tax-exempt interest to find the adjusted amount.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 30
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine the adjusted income and adjusted tax-exempt interest
                  amounts. This gives the total income available for
                  distribution to beneficiaries under AMT rules.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the capital gains included in the income but reduce the
                  amount by any Section 1202 qualified small business stock
                  exclusion that applies under AMT rules.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 32
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any capital gains from the current year that were paid
                  or permanently set aside for charitable purposes. Reduce this
                  amount if any Section 1202 exclusion applies to it.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 33
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report your investment profits or losses here but use the
                  AMT-specific values for the assets you sold. Because the
                  "cost" of an item can be different for AMT than for regular
                  taxes, your gain or loss might be different too.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 34
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line to fix the cost of your property. If you made an
                  adjustment on Line 11 earlier, you must include it here to
                  ensure you aren't being taxed on the same amount twice when
                  calculating your final profit.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 35
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine the totals from Lines 28 through 34. If the math ends
                  up in the negatives, just enter 0.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 36
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of money that the trust <i>must</i> pay out
                  to beneficiaries this year, according to the legal paperwork.
                  These are the no-choice distributions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 37
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This is for any other money actually paid out or credited to
                  beneficiaries that wasn't legally required but happened
                  anyway.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 38
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 36 and 37 together. This represents the total amount
                  of money that left the estate or trust and went into the hands
                  of the beneficiaries this year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 39
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the tax-exempt income included in the total
                  distributions on line 38 (except the amount from line 7). This
                  step separates income that isn’t taxable under AMT.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 40
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 39 from line 38. This gives the portion of the
                  distributions that may qualify for the deduction under AMT
                  rules.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part2-line41-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part2-line41-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part2-line41-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part II showing the income distribution deduction calculated on a minimum tax (AMT) basis for estates and trusts. Line41-Line42"
                    width={760}
                    height={81}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 41
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 29 from line 35. This helps determine the
                  maximum deduction allowed under AMT. If the result is zero or
                  less, enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 42
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Compare line 40 and line 41 and enter the smaller amount here.
                  Record the same amount again on line 24.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III-Alternative Minimum Tax
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part3-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part3-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part III calculating the Alternative Minimum Tax (AMT) for estates and trusts, including adjustments and preference items."
                    width={760}
                    height={292}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 43
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Start with the AMT exemption amount of $30,700. This is the
                  portion of income that is not subject to alternative minimum
                  tax.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 44
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from line 27, which represents the estates or
                  trust’s alternative minimum taxable income.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 45
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The phase-out threshold is $102,500. If income goes above this
                  level, the exemption amount may start to decrease.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 46
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract $102,500 (line 45) from the amount on line 44. If the
                  result is zero or negative, enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 47
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on line 46 by 25%. This figure is used to
                  reduce AMT exemption if income exceeds the limit.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 48
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 47 from line 43 to determine the remaining AMT
                  exemption after any reduction. If the result is zero or less,
                  enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 49
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 48 from line 44. The result shows the income
                  that will actually be subject to alternative minimum tax.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 50
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  If the estate or trust has qualified dividends or certain
                  capital gains, you must go to Part IV to compute this
                  line.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Otherwise:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>If line 49 is $239,100 or less, multiply it by 26%.</li>
                  <li>
                    If line 49 is more than $239,100, multiply it by 28% and
                    then subtract $4,782.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This gives the tentative alternative minimum tax amount.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 51
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any foreign tax credit allowed under AMT rules. This
                  credit reduces the tentative AMT if the estate or trust paid
                  taxes to a foreign country.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 52
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 51 from line 50. The result is the tentative
                  minimum tax after considering the foreign tax credit.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 53
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the regular tax from Form 1041, Schedule G, line 1a, but
                  do not include any foreign tax credit already claimed on
                  Schedule G at line 2a.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 54
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract line 53 from line 52.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>If the result is zero or less, enter 0.</li>
                  <li>
                    If it is positive, this is the Alternative Minimum Tax (AMT)
                    that must be reported on Form 1041, Schedule G, line 1c.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV-Line 50 Computation using Maximum Capital Gains Rates
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part4-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part4-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part IV calculating tax on line 50 using maximum capital gains rates, applying appropriate rates to short- and long-term gains."
                    width={760}
                    height={288}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 55
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Simply enter the amount from line 49 here. This amount
                  represents the estates or trust’s Alternative Minimum Tax
                  (AMT) taxable income that was calculated in the earlier part
                  of the schedule.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 56
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from Schedule D (Form 1041) line 26, Schedule
                  D Tax Worksheet line 13, or the Qualified Dividends Tax
                  Worksheet line 4, depending on which one applies. If AMT
                  adjustments change the capital gain amounts, use the
                  recalculated AMT figures instead.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 57
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from Schedule D (Form 1041), line 18b
                    column (2) after adjusting it for AMT if necessary.
                  </li>
                  <li>
                    If Schedule D wasn’t completed for regular tax or AMT, enter
                    0.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 58
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you didn’t use the Schedule D Tax Worksheet, simply enter
                  the amount from line 56. If you did use the worksheet, add
                  lines 56 and 57, then enter the smaller amount between that
                  total or line 10 of the Schedule D Tax Worksheet.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 59
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Enter the smaller amount between line 55 and line 58.</li>
                  <li>
                    This identifies the portion of income that may qualify for
                    lower capital gain or qualified dividend tax rates.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 60
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Subtract line 59 from line 55.</li>
                  <li>
                    This gives the remaining AMT taxable income that will be
                    taxed at regular AMT rates.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part4-line61-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part4-line61-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part4-line61-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part IV calculating tax on line 50 using maximum capital gains rates, applying appropriate rates to short- and long-term gains. Line 61 to Line 67"
                    width={760}
                    height={196}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 61
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the AMT tax on the amount from line 60. If line 60
                  is $239,100 or less, multiply it by 26%. If it is more than
                  $239,100, multiply it by 28% and then subtract $4,782 from the
                  result.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 62
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the maximum amount of income that can be taxed at the 0%
                  capital gain rate.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 63
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from Schedule D line 27, Schedule D Tax
                  Worksheet line 14, or Qualified Dividends Tax Worksheet line
                  5, depending on which one applies for the regular tax
                  calculation. If none of these were completed, enter the amount
                  from Form 1041 line 23. If that amount is zero or negative,
                  enter 0.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 64
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 63 from line 62. If the result is zero or
                  negative, enter 0.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 65
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount between line 55 and line 56.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 66
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount between line 64 and line 65. This
                  portion of income will be taxed at the 0% rate.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 67
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 66 from line 65. This shows the remaining amount
                  of income that will be taxed at higher capital gain
                  rates.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part4-line68-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part4-line68-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part4-line68-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part IV calculating tax on line 50 using maximum capital gains rates, applying appropriate rates to short- and long-term gains. Line 68-Line 76"
                    width={760}
                    height={244}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 68
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    maximum amount that can be taxed at rates lower than 20%.
                  </span>{' '}
                  This is the maximum amount of income that can be taxed at
                  capital gain rates.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 69
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from line 64.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 70
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from Schedule D line 27, Schedule D Tax
                  Worksheet line 18, or the Qualified Dividends Tax Worksheet
                  line 5, depending on which was used for the regular tax. If
                  none of these were completed, enter the amount from Form 1041
                  line 23. If that amount is zero or negative, enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 71
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from line 69 and line 70.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 72
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 71 from line 68. If the result is zero or less,
                  enter 0.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 73
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Compare line 67 and line 72 and enter the smaller amount.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 74
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on line 73 by 15%. This calculates the tax
                  on that portion of income.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 75
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add line 66 and line 73.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 76
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 75 from line 65. This shows the remaining amount
                  of income that hasn’t yet been taxed at lower capital gain
                  rates.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/schi-f1041-part4-line77-img.webp" />
                  <source srcSet="/assets/images/schi-f1041-part4-line77-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/schi-f1041-part4-line77-img.png"
                    loading="lazy"
                    alt="Form 1041 Schedule I Part IV calculating tax on line 50 using maximum capital gains rates, applying appropriate rates to short- and long-term gains. Line77-Line83"
                    width={760}
                    height={146}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 77
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on line 76 by 20%. This calculates the tax
                  for the portion of income taxed at the 20% capital gain
                  rate.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 78
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from lines 60, 75, and 76.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 79
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 78 from line 55. This shows the remaining income
                  that hasn’t been included in earlier calculations.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 80
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on line 79 by 25% to calculate the tax for
                  that portion.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 81
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add together the amounts from lines 61, 74, 77, and 80. This
                  gives the total AMT tax based on the capital gain rate
                  calculations.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 82
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the AMT tax using a flat rate method on the amount
                  from line 55. If line 55 is $239,100 or less, multiply it by
                  26%. If it is more than $239,100, multiply it by 28% and then
                  subtract $4,782.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 83
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Compare line 81 and line 82 and enter the smaller amount. This
                  is the final AMT tax amount, and it should also be entered on
                  line 50.{' '}
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 2,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Embark on a Smooth Schedule I Filing Journey with TaxZerone
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Complete your Schedule I filing requirements with ease!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1041schi`}
                  >
                    E-File Now
                  </Button>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i1041si.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule I (Form 1041)
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i1041.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 1041
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/instructions/i6251"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 6251
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 84,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the Alternative Minimum Tax (AMT) for estates and
                  trusts?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    The{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Alternative Minimum Tax (AMT)
                    </strong>{' '}
                    is a parallel tax system designed to ensure that estates and
                    trusts pay a minimum amount of tax, even if they qualify for
                    certain deductions or credits under the regular tax system.
                    It recalculates taxable income by adding back items that
                    receive favorable treatment under regular tax rules.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How do I know if an estate or trust is subject to the AMT?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      An estate or trust is subject to the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        Alternative Minimum Tax
                      </strong>{' '}
                      (AMT) if it has income that exceeds the exemption amount
                      or if it claims deductions that significantly reduce its
                      regular tax liability.
                    </Typography>
                    <Typography variant="body1">
                      Common triggers include depreciation, passive activity
                      losses, and tax-exempt interest from private activity
                      bonds.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Do I need to file Schedule I if my estate or trust is
                  tax-exempt?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    If your estate or trust is tax-exempt, you may still need to
                    file{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule I (Form 1041)
                    </strong>{' '}
                    if your alternative minimum taxable income exceeds the
                    applicable exemption amount. This is often the case when the
                    estate or trust has significant unrelated business taxable
                    income (UBTI).
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form1041SchI;
